import React, { forwardRef, useState, useRef } from "react";
import clsx from "clsx";
import {
  Upload,
  X,
  File,
  Image,
  FileText,
  Music,
  Video,
  Archive,
  CheckCircle,
  AlertCircle,
  Loader2,
} from "lucide-react";

// File Upload Types
export interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  preview?: string;
  status: "idle" | "uploading" | "success" | "error";
  progress?: number;
  error?: string;
}

export interface FileUploadProps {
  label?: string;
  helperText?: string;
  error?: string;
  disabled?: boolean;
  multiple?: boolean;
  accept?: string;
  maxSize?: number; // in bytes
  maxFiles?: number;
  dragAndDrop?: boolean;
  withPreview?: boolean;
  async?: boolean;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "success" | "error";
  className?: string;
  labelClassName?: string;
  onFileSelect?: (files: File[]) => void;
  onFileUpload?: (files: UploadedFile[]) => Promise<void>;
  onFileRemove?: (fileId: string) => void;
  id?: string;
}

// File Icon Component
const getFileIcon = (fileType: string) => {
  if (fileType.startsWith("image/")) return <Image className="w-4 h-4" />;
  if (fileType.startsWith("video/")) return <Video className="w-4 h-4" />;
  if (fileType.startsWith("audio/")) return <Music className="w-4 h-4" />;
  if (fileType.includes("pdf") || fileType.includes("document"))
    return <FileText className="w-4 h-4" />;
  if (fileType.includes("zip") || fileType.includes("rar")) return <Archive className="w-4 h-4" />;
  return <File className="w-4 h-4" />;
};

// File Size Formatter
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// File Item Component
interface FileItemProps {
  file: UploadedFile;
  onRemove?: (fileId: string) => void;
  withPreview?: boolean;
}

const FileItem: React.FC<FileItemProps> = ({ file, onRemove, withPreview }) => {
  const getStatusIcon = () => {
    switch (file.status) {
      case "uploading":
        return <Loader2 className="w-4 h-4 animate-spin text-blue-400" />;
      case "success":
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case "error":
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      default:
        return getFileIcon(file.type);
    }
  };

  const getStatusColor = () => {
    switch (file.status) {
      case "success":
        return "border-green-500 bg-green-900/20";
      case "error":
        return "border-red-500 bg-red-900/20";
      case "uploading":
        return "border-blue-500 bg-blue-900/20";
      default:
        return "border-gray-600 bg-gray-800";
    }
  };

  return (
    <div
      className={clsx(
        "flex items-center gap-3 p-3 rounded-md border transition-colors",
        getStatusColor(),
      )}
    >
      {/* Preview or Icon */}
      {withPreview && file.preview && file.type.startsWith("image/") ? (
        <div className="w-10 h-10 rounded-md overflow-hidden bg-gray-700">
          <img src={file.preview} alt={file.name} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="w-10 h-10 rounded-md bg-gray-700 flex items-center justify-center text-gray-400">
          {getFileIcon(file.type)}
        </div>
      )}

      {/* File Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-gray-200 truncate">{file.name}</p>
          {getStatusIcon()}
        </div>
        <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
        {file.error && <p className="text-xs text-red-400 mt-1">{file.error}</p>}
        {file.progress !== undefined && file.status === "uploading" && (
          <div className="mt-2">
            <div className="w-full bg-gray-700 rounded-full h-1">
              <div
                className="bg-tracq-bright-blue h-1 rounded-full transition-all duration-300"
                style={{ width: `${file.progress}%` }}
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">{file.progress}%</p>
          </div>
        )}
      </div>

      {/* Remove Button */}
      {onRemove && file.status !== "uploading" && (
        <button
          onClick={() => onRemove(file.id)}
          className="p-1 hover:bg-gray-600 rounded-md transition-colors"
          type="button"
        >
          <X className="w-4 h-4 text-gray-400 hover:text-white" />
        </button>
      )}
    </div>
  );
};

// Main File Upload Component
export const FileUpload = forwardRef<HTMLInputElement, FileUploadProps>(
  ({
    label,
    helperText,
    error,
    disabled = false,
    multiple = false,
    accept,
    maxSize,
    maxFiles,
    dragAndDrop = false,
    withPreview = false,
    async = false,
    size = "md",
    variant = "default",
    className,
    labelClassName,
    onFileSelect,
    onFileUpload,
    onFileRemove,
    id,
    ...props
  }) => {
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const uploadId = id || `file-upload-${Math.random().toString(36).substr(2, 9)}`;

    const sizeStyles = {
      sm: "h-24",
      md: "h-36",
      lg: "h-48",
    };

    const variantStyles = {
      default: {
        border: "border-gray-700",
        borderHover: "hover:border-tracq-bright-blue",
        borderActive: "border-tracq-bright-blue",
        background: "bg-gray-900",
        backgroundHover: "hover:bg-gray-800",
      },
      success: {
        border: "border-green-500",
        borderHover: "hover:border-green-400",
        borderActive: "border-green-400",
        background: "bg-green-900/20",
        backgroundHover: "hover:bg-green-900/30",
      },
      error: {
        border: "border-red-500",
        borderHover: "hover:border-red-400",
        borderActive: "border-red-400",
        background: "bg-red-900/20",
        backgroundHover: "hover:bg-red-900/30",
      },
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []);
      onFileSelect?.(selectedFiles);
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) {
        setIsDragOver(true);
      }
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      if (disabled) return;

      const droppedFiles = Array.from(e.dataTransfer.files);
      onFileSelect?.(droppedFiles);
    };

    const handleRemoveFile = (fileId: string) => {
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      onFileRemove?.(fileId);
    };

    const handleClick = () => {
      if (!disabled) {
        fileInputRef.current?.click();
      }
    };

    const currentVariant = variantStyles[variant];
    const hasError = !!error;

    const dropzoneClasses = clsx(
      "relative border-2 border-dashed rounded-lg transition-all duration-200 cursor-pointer",
      sizeStyles[size],
      currentVariant.background,
      currentVariant.border,
      {
        [currentVariant.borderActive]: isDragOver,
        [currentVariant.backgroundHover]: !isDragOver && !disabled,
        "cursor-not-allowed opacity-50": disabled,
        "border-red-500 bg-red-900/20": hasError,
      },
    );

    return (
      <div className={clsx("flex flex-col", className)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={uploadId}
            className={clsx(
              "block text-sm font-medium text-gray-300 mb-2",
              disabled && "text-gray-500",
              labelClassName,
            )}
          >
            {label}
          </label>
        )}

        {/* Dropzone */}
        {dragAndDrop ? (
          <div
            className={dropzoneClasses}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleClick}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
              <Upload className="w-8 h-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-300 text-center">
                {isDragOver
                  ? "Dateien hier ablegen"
                  : "Dateien hier ablegen oder klicken zum Auswählen"}
              </p>
              {helperText && <p className="text-xs text-gray-400 mt-1 text-center">{helperText}</p>}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <button
              type="button"
              onClick={handleClick}
              disabled={disabled}
              className={clsx(
                "flex items-center gap-2 px-4 py-2 border border-gray-600 rounded-md",
                "bg-gray-800 text-gray-200 hover:bg-gray-700 transition-colors",
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              <Upload className="w-4 h-4" />
              Datei auswählen
            </button>
          </div>
        )}

        {/* Hidden Input */}
        <input
          ref={fileInputRef}
          type="file"
          id={uploadId}
          multiple={multiple}
          accept={accept}
          onChange={handleFileInputChange}
          className="sr-only"
          {...props}
        />

        {/* File List */}
        {files.length > 0 && (
          <div className="mt-4 space-y-2">
            {files.map((file) => (
              <FileItem
                key={file.id}
                file={file}
                onRemove={handleRemoveFile}
                withPreview={withPreview}
              />
            ))}
          </div>
        )}

        {/* Helper/Error text */}
        {(helperText || error) && (
          <div className={clsx("mt-2 text-sm", error ? "text-red-400" : "text-gray-400")}>
            {error || helperText}
          </div>
        )}
      </div>
    );
  },
);

FileUpload.displayName = "FileUpload";

// Avatar Upload Component
interface AvatarUploadProps extends Omit<FileUploadProps, "dragAndDrop" | "multiple"> {
  currentAvatar?: string;
  onAvatarChange?: (file: File) => void;
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  currentAvatar,
  onAvatarChange,
  ...props
}) => {
  const [preview, setPreview] = useState<string | null>(currentAvatar || null);

  const handleFileSelect = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreview((e.target?.result as string) || null);
        };
        reader.readAsDataURL(file);
        onAvatarChange?.(file);
      }
    }
  };

  return (
    <div className="flex items-center gap-4">
      {/* Avatar Preview */}
      <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
        {preview ? (
          <img src={preview} alt="Avatar preview" className="w-full h-full object-cover" />
        ) : (
          <div className="w-8 h-8 text-gray-400">
            <Image />
          </div>
        )}
      </div>

      {/* Upload Button */}
      <FileUpload
        {...props}
        multiple={false}
        accept="image/*"
        withPreview={false}
        onFileSelect={handleFileSelect}
        className="flex-1"
      />
    </div>
  );
};

// Multi-File Upload Component
export const MultiFileUpload: React.FC<FileUploadProps> = (props) => {
  return <FileUpload {...props} multiple={true} dragAndDrop={true} />;
};

// Async Upload Component
export const AsyncFileUpload: React.FC<FileUploadProps> = (props) => {
  return <FileUpload {...props} async={true} dragAndDrop={true} />;
};
